import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { streamChat } from "@/lib/apiClient";
import { Send, X, Droplets } from "lucide-react";

export default function ChatPanel({ minister, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`sess-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && minister) {
      sessionRef.current = `sess-${minister.id}-${Math.random().toString(36).slice(2)}`;
      setMessages([
        {
          role: "assistant",
          content: `Peace to you. I am ${minister.name}, ${minister.role}. ${minister.focus}. What brought you here today — is there something you'd like prayer for, or a question on your heart?`,
        },
      ]);
      setInput("");
    }
  }, [open, minister]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    await streamChat(
      minister.id,
      sessionRef.current,
      text,
      (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        });
      },
      () => setBusy(false),
      (err) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: err };
          return copy;
        });
        setBusy(false);
      }
    );
  };

  if (!minister) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        data-testid="chat-panel"
        className="w-full sm:max-w-md p-0 border-l border-white/10 bg-[#0A0710] text-white flex flex-col [&>button]:hidden"
      >
        <SheetTitle className="sr-only">Conversation with {minister.name}</SheetTitle>
        <SheetDescription className="sr-only">Autonomous AI apostolic minister — {minister.focus}</SheetDescription>
        {/* Header */}
        <div className="relative p-6 border-b border-white/10 overflow-hidden shrink-0">
          <div className="absolute inset-0 opacity-30 blur-2xl" style={{ background: `radial-gradient(circle at 20% 0%, ${minister.color}, transparent 60%)` }} />
          <div className="relative flex items-center gap-4">
            {minister.photo ? (
              <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border" style={{ borderColor: minister.color }}>
                <img src={minister.photo} alt={minister.name} className="w-full h-full object-cover object-top" />
              </div>
            ) : (
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center font-display text-xl shrink-0 border"
                style={{ borderColor: minister.color, color: minister.color, background: `${minister.color}14` }}
              >
                {minister.initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-display text-xl leading-tight truncate">{minister.name}</p>
              <p className="text-xs text-[#B4ADC1] truncate">{minister.role}</p>
              <span className="inline-flex items-center gap-1.5 mt-1 text-[0.65rem] font-mono uppercase tracking-widest" style={{ color: minister.color }}>
                <Droplets className="w-3 h-3" /> Fruit: {minister.fruit}
              </span>
            </div>
            <button onClick={onClose} data-testid="chat-close" className="text-[#7A708A] hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-4">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gold text-black rounded-br-sm"
                      : "glass text-[#E7E3EE] rounded-bl-sm"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? <TypingDots /> : "")}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Input */}
        <div className="p-4 border-t border-white/10 shrink-0">
          <div className="flex items-end gap-2 glass rounded-2xl p-2">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={`Share your heart with ${minister.name.split(" ").slice(-1)}...`}
              className="flex-1 bg-transparent resize-none outline-none text-sm px-2 py-2 placeholder:text-[#7A708A] max-h-32"
            />
            <button
              data-testid="chat-send"
              onClick={send}
              disabled={busy || !input.trim()}
              className="w-10 h-10 rounded-xl bg-gold text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#e6c14e] transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[0.6rem] text-[#7A708A] mt-2 text-center font-mono">Autonomous AI minister · Confidential · Not a substitute for emergency help (call 988)</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const TypingDots = () => (
  <span className="flex gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-gold"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
      />
    ))}
  </span>
);
